import type {
  DataBuilderActions,
  DatasetInstance,
  DatasetInstanceConfig,
  SelectionListSelectedItem,
} from './data-builder.store';

export type PickedUoaForTarget = {
  unitOfAnalysis: string;
  unitOfAnalysisSelections: SelectionListSelectedItem[];
  sourceInstanceId: string;
};

type PickLastUsedUoaForTargetArgs = {
  configByInstance: Record<string, DatasetInstanceConfig>;
  orderedInstanceIds: string[];
  availableUoaValues: string[];
  excludeInstanceId?: string;
};

type UoaCandidate = {
  instanceId: string;
  config: DatasetInstanceConfig & { unitOfAnalysis: string };
};

export function pickLastUsedUoaForTarget({
  configByInstance,
  orderedInstanceIds,
  availableUoaValues,
  excludeInstanceId,
}: PickLastUsedUoaForTargetArgs): PickedUoaForTarget | null {
  const matchingCandidates = orderedInstanceIds
    .filter((instanceId) => instanceId !== excludeInstanceId)
    .map((instanceId) => ({
      instanceId,
      config: configByInstance[instanceId],
    }))
    .filter((item): item is UoaCandidate => {
      if (item.config === undefined || item.config.unitOfAnalysis === null) {
        return false;
      }
      return availableUoaValues.includes(item.config.unitOfAnalysis);
    });

  if (matchingCandidates.length === 0) {
    return null;
  }

  const latestTimestamp = matchingCandidates.reduce<number | null>(
    (current, candidate) => {
      if (candidate.config.uoaSetAt === null) {
        return current;
      }
      if (current === null || candidate.config.uoaSetAt > current) {
        return candidate.config.uoaSetAt;
      }
      return current;
    },
    null
  );

  const selectedCandidate =
    latestTimestamp === null
      ? (matchingCandidates[0] ?? null)
      : (matchingCandidates.find(
          (candidate) => candidate.config.uoaSetAt === latestTimestamp
        ) ?? null);

  if (selectedCandidate === null) {
    return null;
  }

  return {
    unitOfAnalysis: selectedCandidate.config.unitOfAnalysis,
    unitOfAnalysisSelections: selectedCandidate.config.unitOfAnalysisSelections,
    sourceInstanceId: selectedCandidate.instanceId,
  };
}

type UoaInheritanceSetters = Pick<
  DataBuilderActions,
  | 'setUnitOfAnalysis'
  | 'setUnitOfAnalysisSelections'
  | 'setCompanySelections'
  | 'setCompanySets'
  | 'setCompanyMapper'
>;

/**
 * Apply an inherited UOA to a new dataset so it starts out matching the dataset
 * it was inherited from.
 */
export const applyInheritedUoa = (
  setters: UoaInheritanceSetters,
  targetInstanceId: string,
  picked: PickedUoaForTarget,
  sourceConfig: DatasetInstanceConfig | undefined
): void => {
  setters.setUnitOfAnalysis(targetInstanceId, picked.unitOfAnalysis, {
    uoaSetAt: sourceConfig?.uoaSetAt ?? null,
  });
  setters.setUnitOfAnalysisSelections(
    targetInstanceId,
    picked.unitOfAnalysisSelections
  );

  if (picked.unitOfAnalysis === 'company' && sourceConfig !== undefined) {
    // Carry the source's recency forward: the population setters would
    // otherwise stamp a fresh uoaSetAt, making this auto-inherited dataset
    // outrank the one the user actually configured for future defaulting.
    const inheritedUoaSetAt = { uoaSetAt: sourceConfig.uoaSetAt };
    if (sourceConfig.companySets !== null) {
      setters.setCompanySets(
        targetInstanceId,
        sourceConfig.companySets,
        inheritedUoaSetAt
      );
    } else if (Object.keys(sourceConfig.companySelections).length > 0) {
      setters.setCompanySelections(
        targetInstanceId,
        sourceConfig.companySelections,
        inheritedUoaSetAt
      );
    }
    if (sourceConfig.companyMapper !== null) {
      setters.setCompanyMapper(targetInstanceId, sourceConfig.companyMapper);
    }
  }
};

const haveSameCompanySelectionIds = (
  left: DatasetInstanceConfig['companySelections'],
  right: DatasetInstanceConfig['companySelections']
): boolean => {
  const leftIds = Object.keys(left).sort();
  const rightIds = Object.keys(right).sort();
  if (leftIds.length !== rightIds.length) {
    return false;
  }
  return leftIds.every((id, index) => id === rightIds[index]);
};

/**
 * Other datasets sharing the source's company population — company UOA, no
 * common set, same selected companies. The pool the mapper catch-up draws from
 * (see getCompanyMapperCatchUpTargetIds); never used to propagate edits.
 */
const getSameCompanyPopulationSiblingIds = ({
  sourceInstanceId,
  configByInstance,
  datasetInstances,
}: {
  sourceInstanceId: string;
  configByInstance: Record<string, DatasetInstanceConfig>;
  datasetInstances: DatasetInstance[];
}): string[] => {
  const sourceConfig = configByInstance[sourceInstanceId];
  if (sourceConfig === undefined) {
    return [];
  }
  // An empty selection is not a shareable population: datasets with no selected
  // companies (e.g. an admin ref-table dataset) all have `{}` here, and must not
  // be treated as siblings of one another — syncing would clobber an unrelated
  // dataset's mapper.
  if (Object.keys(sourceConfig.companySelections).length === 0) {
    return [];
  }
  return datasetInstances
    .filter((instance) => {
      if (instance.instanceId === sourceInstanceId) {
        return false;
      }
      const targetConfig = configByInstance[instance.instanceId];
      return (
        targetConfig !== undefined &&
        targetConfig.unitOfAnalysis === 'company' &&
        targetConfig.companySets === null &&
        haveSameCompanySelectionIds(
          sourceConfig.companySelections,
          targetConfig.companySelections
        )
      );
    })
    .map((instance) => instance.instanceId);
};

/**
 * Siblings waiting to receive the source's resolved mapper: same company
 * population, but no mapper of their own yet (e.g. a dataset that defaulted from
 * the source mid-resolution). A sibling with its own resolved mapper is excluded
 * — catch-up never overwrites an already-configured dataset.
 */
export const getCompanyMapperCatchUpTargetIds = ({
  sourceInstanceId,
  configByInstance,
  datasetInstances,
}: {
  sourceInstanceId: string;
  configByInstance: Record<string, DatasetInstanceConfig>;
  datasetInstances: DatasetInstance[];
}): string[] => {
  const sourceConfig = configByInstance[sourceInstanceId];
  if (sourceConfig === undefined || sourceConfig.unitOfAnalysis !== 'company') {
    return [];
  }

  return getSameCompanyPopulationSiblingIds({
    sourceInstanceId,
    configByInstance,
    datasetInstances,
  }).filter(
    (instanceId) => configByInstance[instanceId]?.companyMapper == null
  );
};
